import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { queryError } from "../services/queryError";
import {
  fetchBoxById,
  fetchCategories,
  fetchCategoryProducts,
  fetchGlobalBoxes,
  fetchGlobalProducts,
  fetchProducts,
  fetchTrendingProducts,
  fetchUserBoxes,
} from "../services/productService";

// Helper to unify error handling
const useQueryWithError = (query, { showToast, navigate } = {}) => {
  useEffect(() => {
    if (query.isError) {
      queryError(query.error, showToast, navigate);
    }
  }, [query.isError, query.error, showToast, navigate]);

  return query;
};

// 🚀 Optimized options for offline-first pattern (v5)
const staticDataOptions = {
  staleTime: 60 * 60 * 1000, // 60 min (Categories, Global Lists)
  gcTime: 24 * 60 * 60 * 1000, // 24h persistent cache
  refetchOnReconnect: true,
  refetchOnWindowFocus: false,
};

const dynamicDataOptions = {
  staleTime: 30 * 60 * 1000, // 30 min (Trending, Paginated Products)
  gcTime: 24 * 60 * 60 * 1000, // 24h persistent cache
  refetchOnReconnect: true,
  refetchOnWindowFocus: false,
};

export const useProducts = (page = 1, size = 20, search = "", options = {}) => {
  const query = useQuery({
    queryKey: ["products", { page, size, search }],
    queryFn: () => fetchProducts({ page, size, search }),
    ...dynamicDataOptions,
    ...options,
  });

  return useQueryWithError(query, options);
};
export const useInfiniteProducts = (
  size = 20,
  categoryId = undefined,
  search = "",
  options = {},
) => {
  const query = useInfiniteQuery({
    queryKey: ["products", "infinite", { size, search, categoryId }],
    queryFn: ({ pageParam = 1 }) =>
      fetchProducts({ page: pageParam, size, categoryId, search }),

    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.pagination;
      if (!pagination) return undefined;

      return pagination.currentPage < pagination.totalPages
        ? pagination.currentPage + 1
        : undefined;
    },

    ...dynamicDataOptions,
    ...options, // Allow options to override defaults
  });

  return useQueryWithError(query, options);
};

export const useGlobalProducts = (options = {}) => {
  const query = useQuery({
    queryKey: ["products", "global"],
    queryFn: fetchGlobalProducts,
    ...staticDataOptions,
  });

  return useQueryWithError(query, options);
};

export const useCategories = (options = {}) => {
  const query = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    ...staticDataOptions,
  });

  return useQueryWithError(query, options);
};

export const useTrendingProducts = (options = {}) => {
  const query = useQuery({
    queryKey: ["products", "trending"],
    queryFn: fetchTrendingProducts,
    ...dynamicDataOptions,
  });

  return useQueryWithError(query, options);
};

export const useCategoryProducts = (categoryId, options = {}) => {
  const query = useQuery({
    queryKey: ["products", "category", categoryId],
    queryFn: () => fetchCategoryProducts(categoryId),
    enabled: !!categoryId,
    retry: 1,
    ...dynamicDataOptions,
  });

  return useQueryWithError(query, options);
};

export const useGlobalBoxes = (options = {}) => {
  const query = useQuery({
    queryKey: ["bundles", "global"],
    queryFn: fetchGlobalBoxes,
    ...staticDataOptions,
  });

  return useQueryWithError(query, options);
};

export const useCustomerBoxes = (options = {}) => {
  const query = useQuery({
    queryKey: ["bundles", "user"],
    queryFn: fetchUserBoxes,
    ...staticDataOptions,
    enabled: options?.enabled,
  });

  return useQueryWithError(query, options);
};

export const useBoxDetails = (boxId, options = {}) => {
  const query = useQuery({
    queryKey: ["bundles", "detail", boxId],
    queryFn: () => fetchBoxById(boxId),
    ...staticDataOptions,
    enabled: !!boxId && (options?.enabled ?? true),
  });

  return useQueryWithError(query, options);
};
